import { type FC, type ReactNode, useId } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Box, Divider, Expandable } from '@eneco/sparky';

import { Row } from './PriceDetails.Row';
import { type ContentProps, type Details, type FooterProps } from './PriceDetails.types';

export const ExpandableRowLayout: FC<{ summary?: Details; children: ReactNode }> = ({ summary, children }) => {
  const id = `${useId()}-${summary?.text?.replaceAll(' ', '-')}`;

  return (
    <Expandable>
      <Box paddingRight="2">
        <Expandable.Trigger isTransparent aria-labelledby={id}>
          <Row id={id} {...summary} />
        </Expandable.Trigger>
      </Box>
      <Divider emphasis="low" />
      <Expandable.Content>{children}</Expandable.Content>
    </Expandable>
  );
};

export const Content: FC<ContentProps> = ({ summary, details, text }) => {
  if (!details && !text) {
    return (
      <Box paddingRight="10">
        <Box paddingRight="1">
          <Row {...summary} />
        </Box>
      </Box>
    );
  }

  return (
    <ExpandableRowLayout summary={summary}>
      {details?.map((detail, index) => (
        <Box key={index}>
          <Row isNested {...detail} />
        </Box>
      ))}

      {text && (
        <Box padding="3">
          <RichText html={text} />
        </Box>
      )}
    </ExpandableRowLayout>
  );
};

export const Footer: FC<FooterProps> = ({ summary, details }) => (
  <Box backgroundColor="backgroundSecondary">
    {details?.map((detail, index) => (
      <Box key={index}>
        <Row isNested {...detail} />
      </Box>
    ))}

    <Divider isDecorative={true} emphasis="high" />
    <Box paddingRight="10">
      <Box paddingRight="1">
        <Row {...summary} highlightValue />
      </Box>
    </Box>
  </Box>
);
